// libraries
import React, { useState, useEffect } from 'react'

// components
import Section from '/react4xp/components/Section'
import OverviewTopPart from '/react4xp/components/OverviewTopPart'
import Button from '/react4xp/components/Button'
import ProductCard from '/react4xp/components/ProductCard'

// svg
import { UxArrowDown } from '/react4xp/components/Svg/Ux/Arrow'

// prop types
export interface ProductsOverviewWrapperProps {
	title?: string
	endpoint?: string
	categories?: {
		id: string
		title: string
		name: string
	}[]
	startValues?: {
		categories?: string[],
		search?: string,
		page?: number,
		pageSize?: number,
	}
	strings?: {
		search?: string
		searchButton?: string
		noResultsFound?: string
		loadMore?: string
	}
}

export default function ProductsOverviewWrapper({
	title,
	endpoint,
	categories,
	startValues,
	strings
}: ProductsOverviewWrapperProps) {

	const [products, setProducts] = useState<any[]>([])
    const [page, setPage] = useState(startValues?.page || 1)
    const [inputValue, setInputValue] = useState(startValues?.search || '')
	const [searchTerm, setSearchTerm] = useState(startValues?.search || '')
    const [selectedCategories, setSelectedCategories] = useState<any[]>(startValues?.categories && categories.filter((category)=> startValues.categories.includes(category.name)) || [])
    const [hasNextPage, setHasNextPage] = useState(false)
    const [loadMore, setLoadMore] = useState(false)

	useEffect(() => {
        handleFetchArticles()
	}, [searchTerm, selectedCategories, page])

    const handleFetchArticles = async ()=> {
        
        if(!endpoint) return;
        
        const origin = window.location.origin;
        const url = new URL(`${origin}${endpoint}`);
        const queryParams = new URLSearchParams();

        if (page) queryParams.append('page', String(page));
        if (startValues?.pageSize) queryParams.append('pageSize', String(startValues?.pageSize));
        if (searchTerm) queryParams.append('search', searchTerm);
        if (selectedCategories.length) {
            selectedCategories.forEach(category => {
                queryParams.append('categories', category.name);
            });
        }
    
        url.search = queryParams.toString();

        updateCurrentUrl()
        
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }
            const data = await response.json();
            
            if(loadMore){
                setProducts([...products, ...data.hits]);
                setLoadMore(false)
            }else{
                setProducts(data.hits);
            }

            setHasNextPage(data.hasNextPage);

        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    }

    const handleSelectCategoryFilter = (category) => {
        setPage(1);
        setSelectedCategories(prev => {
            const tagExists = prev.some(item => item.id === category.id);
            if (tagExists) {
                return prev.filter(item => item.id !== category.id);
            } else {
                return [...prev, { id: category.id, title: category.title, name: category.name }];
            }
        });
    };

	const handleSearchTerm = () => {
        setPage(1)
        setSearchTerm(inputValue)

        setTimeout(() => {
			const searchResultsElement = document.getElementById('search-results')
			if (searchResultsElement) {
				const headerOffset = 0
				const elementPosition = searchResultsElement.getBoundingClientRect().top + window.scrollY
				const offsetPosition = elementPosition - headerOffset
	
				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth'
				})
			}
		}, 100)
	}
    
	const handleLoadMoreProducts = () => {
        if(hasNextPage){
            setLoadMore(true)
            setPage(page + 1)
        }
    }

    const updateCurrentUrl = () => {
        const origin = window.location.origin;
        const updatedUrl = new URL(`${origin}${window.location.pathname}`);
        const queryParams = new URLSearchParams();

        if (searchTerm) queryParams.append('search', searchTerm);
        if (selectedCategories.length) {
            selectedCategories.forEach(category => {
                queryParams.append('categories', category.name);
            });
        }

        updatedUrl.search = queryParams.toString();
        window.history.pushState({}, '', updatedUrl);
    
    }

	return (
		<main
			id='main'
			className='page-overview'
		>

			<OverviewTopPart
				title={title}
				search={{
					label: strings.search,
					searchButtonText: strings.searchButton,
					value: inputValue,
					onSearch: handleSearchTerm,
					onChange: (e) => {
						setInputValue(e.target.value)
					}
				}}
			/>

			<Section marginTop='none'>
				<div className='container'>

					<div id='search-results' className='overview-grid'>

						{products.length > 0 ? (
							<div className='overview-grid__wrapper'>
								{products.map((product, i) => (
									<ProductCard
										key={i}
										tag={product.tag}
										href={product.url?.href}
										external={false}
										src={product.image?.link} // 400px x 400px
                                        srcMedium={product.imageMedium?.link} // 500px x 500px
										alt={product.image?.alt}
										width={400}
										sizes='( max-width: 992px ) 100vw, 400px'
										title={product.title}
									/>
								))}
							</div>
						) : (
							<div className='overview-grid__no-results'>
								<h2 className='h1-hero-small'>
									{strings.noResultsFound}
								</h2>
							</div>
						)}

						{hasNextPage && (
							<div className='overview-grid__load-more'>
								<Button onClick={handleLoadMoreProducts}>
									{strings.loadMore} <UxArrowDown />
								</Button>
							</div>
						)}

					</div>

				</div>
			</Section>
			
		</main>
	)
}
